<div class="dashboard-page">
    <!-- Hero Section -->
    <section class="hero">
        <div class="hero-route"></div>
        <div class="hero-content">
            <div class="hero-copy">
                <h2>{{ heroGreeting() }}</h2>
                <p>Here is today's overview of sales, reservations and network performance.</p>
            </div>
            <div class="hero-actions">
                <p-date-picker
                    #mainDatePicker
                    [(ngModel)]="rangeDates"
                    selectionMode="range"
                    [readonlyInput]="true"
                    [showIcon]="true"
                    [firstDayOfWeek]="1"
                    [numberOfMonths]="2"
                    [showOnFocus]="true"
                    [hideOnDateTimeSelect]="false"
                    [dateFormat]="'dd.mm.yy'"
                    styleClass="hero-btn"
                    [inputStyleClass]="'hero-input'"
                    [panelStyleClass]="'custom-datepicker-panel'"
                    [inputStyle]="{ width: '240px' }"
                    [appendTo]="'body'"
                >
                    <ng-template pTemplate="header">
                        <div class="custom-datepicker-header">
                            <div class="date-range-list">
                                <div
                                    *ngFor="let range of dateRanges"
                                    [class.selected]="selectedDateRange() === range.value"
                                    class="date-range-item"
                                    (click)="setDateRange(range.value)"
                                >
                                    {{ range.label }}
                                </div>
                            </div>
                        </div>
                    </ng-template>
                    <ng-template pTemplate="footer">
                        <div class="custom-datepicker-footer flex justify-between mt-3">
                            <div class="pt-2">
                                <p-checkbox
                                    [(ngModel)]="flightDateBased"
                                    [binary]="true"
                                    inputId="flightDateBased"
                                    (onChange)="defineReportButtonItems()"
                                />
                                <label for="flightDateBased" class="ml-2 text-sm text-gray-700">Search Flight Date Based</label>
                            </div>
                            <div class="flex gap-2">
                                <p-button label="Cancel" [outlined]="true" (click)="cancel()" />
                                <p-button label="Apply" (click)="apply()" />
                            </div>
                        </div>
                    </ng-template>
                </p-date-picker>

                @if (!isSupplierAdmin()) {
                    <p-button
                        label="OTA Performances"
                        icon="analytics"
                        [outlined]="true"
                        styleClass="hero-btn"
                        routerLink="/ota-performances"
                    />
                }
            </div>
        </div>
    </section>

    <!-- KPI Cards -->
    <section class="kpis">
        <article class="kpi">
            <div class="kpi-top">
                <span class="kpi-icon tone-purple"><span class="material-icons-outlined">confirmation_number</span></span>
                Total Reservations
            </div>
            <div class="kpi-value">{{ totalReservationValue() }}</div>
        </article>
        <article class="kpi">
            <div class="kpi-top">
                <span class="kpi-icon tone-green"><span class="material-icons-outlined">payments</span></span>
                {{ isSupplierAdmin() ? 'Total Revenue' : "Today's Revenue" }}
            </div>
            <div class="kpi-value">{{ salesInformationData()?.revenue?.amount | currency: salesInformationData()?.revenue?.currency }}</div>
        </article>
        <article class="kpi">
            <div class="kpi-top">
                <span class="kpi-icon tone-red"><span class="material-icons-outlined">cancel</span></span>
                Cancelled
            </div>
            <div class="kpi-value">{{ salesInformationData()?.cancelled }}</div>
        </article>
        <article class="kpi">
            <div class="kpi-top">
                <span class="kpi-icon tone-blue"><span class="material-icons-outlined">person</span></span>
                Adult
            </div>
            <div class="kpi-value">{{ salesInformationData()?.adultSeat }}</div>
        </article>
        <article class="kpi">
            <div class="kpi-top">
                <span class="kpi-icon tone-cyan"><span class="material-icons-outlined">person</span></span>
                Child
            </div>
            <div class="kpi-value">{{ salesInformationData()?.childSeat }}</div>
        </article>
        <article class="kpi">
            <div class="kpi-top">
                <span class="kpi-icon tone-amber"><span class="material-icons-outlined">person</span></span>
                Infant
            </div>
            <div class="kpi-value">{{ salesInformationData()?.infantSeat }}</div>
        </article>
    </section>

    <!-- Main Grid: Agent Information + Best Seller Routes Panel -->
    <div class="dashboard-grid" [class.single-column]="isSupplierAdmin()">
        @if (!isSupplierAdmin()) {
            <app-agent-information [agentInformationData]="agentInformationData()?.results" [loading]="loading()" />
        }
        <app-best-seller-route-panel [bestSellerRouteData]="bestSellerRouteData()?.results" [loading]="loading()" />
    </div>

    <!-- Route Performance Table -->
    @if (!isSupplierAdmin()) {
        <app-best-seller-route class="block mt-4" [bestSellerRouteData]="bestSellerRouteData()?.results" [loading]="loading()" />
    }

    <!-- Section Divider: Operational Statistics -->
    <div class="section-divider">
        <h3>Operational Statistics</h3>
        <span>Cancellation, change and airline performance</span>
    </div>
    <div class="stats-grid" [class.single-column]="isSupplierAdmin()">
        @if (!isSupplierAdmin()) {
            <app-agent-reservation-cancel-change [agentBiStatisticData]="agentBiStatisticsData()?.results" [loading]="loading()" />
        }
        <app-airline-statistics [airlineStatisticsData]="airlineStatisticsData()?.results" [loading]="loading()" />
    </div>

    <!-- Insight Strip -->
    @if (!isSupplierAdmin()) {
        <div class="insight-strip">
            <div class="insight-icon">
                <span class="material-icons-outlined">insights</span>
            </div>
            <div class="insight-copy">
                <strong>Operational insight</strong>
                <span>Monitor cancellation trends and airline performance for operational efficiency.</span>
            </div>
        </div>
    }

    @if (!isSupplierAdmin()) {
        <!-- Section Divider: Sales Analytics -->
        <div class="section-divider">
            <h3>Sales Analytics</h3>
            <span>Channel mix, monthly trend and provider performance</span>
        </div>

        <!-- Last Year Comparison + Monthly Sales Chart -->
        <div class="sales-report-grid">
            <app-last-year-comparison
                [totalReservations]="totalReservationValue()"
                [totalSeat]="totalSeat()"
                [lastYearReservations]="salesPerformanceData()?.lastYearReservation ?? 0"
                [lastYearSeats]="salesPerformanceData()?.lastYearSeat ?? 0"
                [comparisonReservation]="salesPerformanceData()?.comparisonReservation"
                [comparisonSeat]="salesPerformanceData()?.comparisonSeat"
                [loading]="loading()"
            />
            <app-monthly-sales-chart [monthlyData]="salesPerformanceData()?.thisYear?.monthly" [loading]="loading()" />
        </div>

        <!-- Analytics Grid: Doughnut + Stacked Bar -->
        <div class="analytics-grid">
            <app-card class="block" icon="pie_chart" title="All Sales Breakdown" subtitle="Total sales by channel" iconGradient="from-teal-500 to-green-600">
                <app-doughnut-chart [salesChannelData]="salesChannelData()?.total" [loading]="loading()" />
            </app-card>
            <app-card class="block" icon="bar_chart" title="Monthly Sales Channel" subtitle="Stacked channel performance" iconGradient="from-violet-500 to-purple-600">
                <app-stacked-bar-chart [salesChannelMonthlyData]="salesChannelData()?.monthly" [loading]="loading()" />
            </app-card>
        </div>
    }

    <!-- Provider Sales Performance -->
    <app-card class="block mt-4" icon="trending_up" title="Provider Sales Performance" subtitle="Total sales across connected providers" iconGradient="from-amber-500 to-orange-600">
        <app-basic-chart [providerInformationData]="providerInformationData()?.salesNumber" [loading]="loading()" />
    </app-card>
</div>
